<script lang="ts">
  // Right-edge phase control, Master-Duel style: a phase ORB (current phase + turn + countdown ring)
  // that, when clicked, opens a small popover of the LEGAL destinations (To Battle / Main 2 / End
  // Turn), so you pick where to go rather than stepping one phase at a time, and from Main 1 you can
  // jump straight to End. A slim vertical phase track sits above it for orientation, and a one-click
  // AUTO toggle sits below (auto-pass optional response/chain windows vs. stop at each, flipped
  // situationally, so it's always visible, never buried in a menu). Purely presentational.
  import { PHASE } from "@ygoevo/protocol";

  type PhaseTarget = "bp" | "m2" | "ep";
  interface Advance { label: string; target: PhaseTarget; choice: unknown; accent: "gold" | "cyan" | "magenta" }

  let {
    turn,
    phaseMask,
    myTurn,
    timerPct = 100,
    timerSecs = 0,
    timerMine = myTurn,
    timerActive = false,
    timerEnabled = false,
    advances = [],
    onadvance,
  }: {
    turn: number;
    phaseMask: number;
    myTurn: boolean;
    timerPct?: number;
    timerSecs?: number;
    timerMine?: boolean;
    timerActive?: boolean;
    timerEnabled?: boolean;
    advances?: Advance[];
    onadvance?: (choice: unknown) => void;
  } = $props();

  const BATTLE_MASK = PHASE.BATTLE_START | PHASE.BATTLE_STEP | PHASE.DAMAGE | PHASE.DAMAGE_CAL | PHASE.BATTLE;
  const PILLS: { key: string; label: string; mask: number }[] = [
    { key: "dp", label: "DP", mask: PHASE.DRAW },
    { key: "sp", label: "SP", mask: PHASE.STANDBY },
    { key: "m1", label: "M1", mask: PHASE.MAIN1 },
    { key: "bp", label: "BP", mask: BATTLE_MASK },
    { key: "m2", label: "M2", mask: PHASE.MAIN2 },
    { key: "ep", label: "EP", mask: PHASE.END },
  ];
  const curIdx = $derived(PILLS.findIndex((p) => (phaseMask & p.mask) !== 0));
  const curLabel = $derived(PILLS[curIdx]?.label ?? "-");

  const R = 35;
  const C = 2 * Math.PI * R;
  const low = $derived(timerEnabled && timerActive && timerSecs <= 10);
  const ring = $derived(low ? "var(--color-magenta)" : timerMine ? "var(--color-gold)" : "var(--color-cyan)");
  const orbBg = $derived(
    myTurn
      ? "radial-gradient(circle at 38% 28%, #ffe9a8, #ffc83a 40%, #b8841f 74%, #6e4a14)"
      : "radial-gradient(circle at 38% 28%, #bfeaff, #19e0ff 42%, #0a6f88 76%, #063543)",
  );
  const actionable = $derived(myTurn && advances.length > 0);

  let menuOpen = $state(false);
  $effect(() => { if (!actionable) menuOpen = false; });

  function orbClick() {
    if (!actionable) return;
    if (advances.length === 1) { onadvance?.(advances[0]!.choice); return; }
    menuOpen = !menuOpen;
  }
  function pick(a: Advance) { onadvance?.(a.choice); menuOpen = false; }

  const ACCENT: Record<string, string> = {
    gold: "border-gold/55 text-gold hover:bg-gold/15",
    cyan: "border-cyan/55 text-cyan hover:bg-cyan/15",
    magenta: "border-magenta/55 text-magenta hover:bg-magenta/15",
  };
  // literal text-color classes for the single-advance affordance label (Tailwind v4: no constructed classes)
  const ACCENT_TEXT: Record<Advance["accent"], string> = {
    gold: "text-gold",
    cyan: "text-cyan",
    magenta: "text-magenta",
  };
</script>

<div class="flex flex-col items-center gap-1.5">
  <!-- orb + destination popover (the orb alone shows the phase, no separate track) -->
  <div class="relative grid place-items-center" style="width:calc(5.9rem*var(--ui));height:calc(5.9rem*var(--ui))">
    <svg viewBox="0 0 100 100" class="absolute inset-0 size-full -rotate-90">
      <circle cx="50" cy="50" r={R} fill="none" stroke="#ffffff" stroke-width="3.5" opacity="0.08" />
      <circle
        cx="50" cy="50" r={R} fill="none" stroke={ring} stroke-width="3.5" stroke-linecap="round"
        stroke-dasharray={C} stroke-dashoffset={C * (1 - Math.max(0, Math.min(1, timerPct / 100)))}
        class="transition-[stroke-dashoffset] duration-1000 ease-linear" style="filter:drop-shadow(0 0 4px {ring})"
      />
    </svg>

    <button
      type="button"
      onclick={orbClick}
      disabled={!actionable}
      title={actionable ? "Advance phase" : ""}
      class="relative grid place-items-center rounded-full text-center transition-transform duration-150 {actionable ? 'cursor-pointer hover:scale-105 active:scale-95' : 'cursor-default'}"
      style="width:calc(4.3rem*var(--ui));height:calc(4.3rem*var(--ui));background:{orbBg};box-shadow:0 0 20px color-mix(in oklab,{ring} 42%,transparent),inset 0 2px 7px rgba(255,255,255,.55),inset 0 -7px 14px rgba(0,0,0,.42)"
    >
      <span class="pointer-events-none absolute inset-x-3 top-2 rounded-full bg-white/35 blur-[3px]" style="height:calc(0.9rem*var(--ui))"></span>
      <span class="relative flex flex-col leading-none text-void">
        <span class="font-display font-bold uppercase tracking-[0.2em] opacity-80" style="font-size:calc(0.46rem*var(--ui))">Turn {turn}</span>
        <span class="mt-0.5 font-display font-extrabold uppercase tracking-wide drop-shadow-[0_1px_1px_rgba(255,255,255,.4)]" style="font-size:calc(1.28rem*var(--ui))">{curLabel}</span>
        <span class="mt-0.5 font-display font-bold tabular-nums opacity-75" title={timerEnabled ? (timerMine ? "Your time remaining" : "Opponent time remaining") : "Timer unavailable"} style="font-size:calc(0.6rem*var(--ui))">
          {timerEnabled ? `${timerMine ? "YOU" : "OPP"} · ${timerSecs}s` : "—"}
        </span>
      </span>
      {#if actionable}
        <span class="pointer-events-none absolute -inset-1 rounded-full" style="box-shadow:0 0 0 2px color-mix(in oklab,{ring} 70%,transparent);animation:orbpulse 1.6s ease-in-out infinite"></span>
      {/if}
    </button>

    {#if menuOpen}
      <!-- destinations open to the LEFT, into the board, so they're never clipped by the edge -->
      <button class="fixed inset-0 z-40 cursor-default" aria-label="Close" onclick={() => (menuOpen = false)}></button>
      <div class="absolute top-1/2 z-50 flex -translate-y-1/2 flex-col chamfer bg-void/95 ring-1 ring-gold/40 backdrop-blur" style="--c:8px;right:calc(5.8rem*var(--ui));min-width:calc(9rem*var(--ui));gap:calc(0.375rem*var(--ui));padding:calc(0.5rem*var(--ui))">
        <span class="px-1 pb-0.5 font-display uppercase tracking-[0.18em] text-ink-dim/60" style="font-size:calc(0.5rem*var(--ui))">Go to</span>
        {#each advances as a (a.target)}
          <button class="chamfer-btn border bg-void/60 text-right font-display font-bold uppercase tracking-wider transition-colors {ACCENT[a.accent]}" style="--c:4px;padding:calc(0.5rem*var(--ui)) calc(0.75rem*var(--ui));font-size:calc(0.75rem*var(--ui))" onclick={() => pick(a)}>{a.label}</button>
        {/each}
      </div>
    {/if}
  </div>

  <!-- single-advance affordance: when one action fires the orb directly, name it -->
  {#if actionable && advances.length === 1}
    <span
      class="font-display uppercase tracking-[0.16em] {ACCENT_TEXT[advances[0]!.accent]}"
      style="font-size:calc(0.6rem*var(--ui))"
    >▸ {advances[0]!.label}</span>
  {/if}
</div>

<style>
  @keyframes orbpulse {
    0%, 100% { opacity: 0.9; transform: scale(1); }
    50% { opacity: 0.25; transform: scale(1.08); }
  }
</style>
